﻿import { ProductInteraction } from './ProductInteraction.js';

export const EditScope = {
  front: 'front',
  door: 'door',
  panel: 'panel'
};

export const ComponentType = {
  panel: {
    id: 'panel',
    useEditScope: true,
    getValue: (apiProduct) => apiProduct.f,
    getAllUsedValues: (product) => product.getAllPrimaryFinishes(),
    handleDrawingClick: (interaction, event) => interaction.handleFrontPanelsClick(event),
    showGalleryFilterButton: true,
  },
  frame: {
    id: 'frame',
    useEditScope: false,
    defaultEditScope: 'front',
    getValue: (apiProduct) => apiProduct.frameF,
    getAllUsedValues: (product) => product.getAllFrameFinishes(true),
    handleDrawingClick: (interaction, event) => interaction.handleFrontFrameClick(event),
    showGalleryFilterButton: false,
  },
  track: {
    id: 'track',
    useEditScope: false,
    defaultEditScope: 'front',
    getValue: (apiProduct) => apiProduct.topTrack.f,
    getAllUsedValues: (product) => product.getAllTrackFinishes(true),
    handleDrawingClick: (interaction, event) => interaction.handleTrackClick(event),
    showGalleryFilterButton: false,
  },
  layout: {
    id: 'layout',
    useEditScope: true,
    getValue: (apiProduct) => apiProduct.panelLayout,
    getAllUsedValues: (product) => product.getAllPanelLayouts(true),
    handleDrawingClick: (interaction, event) => interaction.handleFrontLayoutClick(event),
    showGalleryFilterButton: false,
  },
};

export class DoorInteraction extends ProductInteraction {
  constructor() {
    super();
    this._editScope = EditScope.panel;
    this._componentType = ComponentType.panel;
    /* We need to create a wrapper object for the removeEventListener call to actually remove the
     * listener. This happens inside the drawing engine's toggleDisplayObjectInteractive method. */
    this.doorFrameClickHandler = { handler: event => this.handleDoorFrameClick(event) };
    this.panelClickHandler = { handler: event => this.handlePanelClick(event) };
    this.doorPanelsClickHandler = { handler: event => this.handleDoorPanelsClick(event) };
    this.doorLayoutClickHandler = { handler: event => this.handleDoorLayoutClick(event) };
  }

  toggleInteractiveOnEachDoorPanel(door, on, clickHandler) {
    door.core.panels.forEach(element => this.toggleInteractive(element.displayObject, on, clickHandler));
  }

  activateInteraction(on) {
    super.activateInteraction(on);
    if (!this.product) {
      return;
    }

    switch (this.componentType) {
      case ComponentType.frame:
        if (this.editScope === EditScope.door) {
          this.toggleInteractive(this.product.displayObject, on, this.doorFrameClickHandler.handler);
        } else {
          this.unsupportedTypeAndScope();
        }
        break;

      case ComponentType.panel:
        switch (this.editScope) {
          case EditScope.panel:
            this.toggleInteractiveOnEachDoorPanel(this.product, on, this.panelClickHandler.handler);
            break;
          case EditScope.door:
            this.toggleInteractive(this.product.displayObject, on, this.doorPanelsClickHandler.handler);
            break;
          default:
            this.unsupportedTypeAndScope();
            break;
        }
        break;

      case ComponentType.layout:
        if (this.editScope === EditScope.door) {
          this.toggleInteractive(this.product.displayObject, on, this.panelLayoutClickHandler.handler);
        } else {
          this.unsupportedTypeAndScope();
        }
        break;

      default:
        this.unsupportedTypeAndScope();
        break;
    }
  }

  /** Raises the onPanelFinishChanged event. */
  doOnPanelFinishChanged(panel, oldFinish) {
    if (this.onPanelFinishChanged) {
      this.onPanelFinishChanged(this, oldFinish, panel);
    }
  }

  /** Raises the onFrameFinishChanged event. */
  doOnDoorFrameFinishChanged(door, oldFinish) {
    if (this.onFrameFinishChanged) {
      this.onFrameFinishChanged(this, oldFinish, door.frame);
    }
  }

  doOnDoorLayoutChanged(door, oldLayout) {
    if (this.onDoorLayoutChanged) {
      this.onDoorLayoutChanged(this, oldLayout, door);
    }
  }

  /** Click on a single panel. */
  handlePanelClick(event) {
    if (this.selectedValue) {
      const panel = event.currentTarget.dataComponent;
      panel.replaceFinish(this.selectedValue, (sender, args) => {
        this.refreshInteractive(args.oldDisplayObject, panel.displayObject, this.panelClickHandler.handler);
        this.doOnProductChanged(panel);
        this.doOnPanelFinishChanged(panel, args.oldFinish);
      });
      this.doOnInteraction(event);
    }
  }

  /** Click on a door, and we need to update all the panels. */
  handleDoorPanelsClick(event) {
    if (this.selectedValue) {
      const door = event.currentTarget.dataComponent;
      let wasChanged = false;
      door.replaceAllPanelsFinish(this.selectedValue, (dor, args) => {
        this.doOnPanelFinishChanged(args.panel, args.oldFinish);
        wasChanged = true;
      });
      if (wasChanged) {
        this.doOnProductChanged(door);
      }
      this.doOnInteraction(event);
    }
  }

/** Click on a single door, and we need to update the door's frame finish. */
  handleDoorFrameClick(event) {
    if (this.selectedValue) {
      const door = event.currentTarget.dataComponent;
      door.replaceFrameFinish(this.selectedValue, (dor, { oldFinish }) => {
        this.doOnProductChanged(dor.frame);
        this.doOnDoorFrameFinishChanged(dor, oldFinish);
      });
      this.doOnInteraction(event);
    }
  }

/** Click on a single door, and we need to update the door's layout (design). */
  handleDoorLayoutClick(event) {
    if (this.selectedValue !== null) {
      const door = event.currentTarget.dataComponent;
      door.replaceLayout(this.selectedValue, (dor, { oldLayout }) => {
        this.doOnProductChanged(dor);
        this.doOnDoorLayoutChanged(dor, oldLayout);
      });
      this.doOnInteraction(event);
    }
  }
}

export class DoorFrontInteraction extends DoorInteraction {
  constructor() {
    super();
    /* We need to create a wrapper object for the removeEventListener call to actually remove the
     * listener. This happens inside the drawing engine's toggleDisplayObjectInteractive method. */
    this.frontPanelsClickHandler = { handler: event => this.handleFrontPanelsClick(event) };
    this.frontFrameClickHandler = { handler: event => this.handleFrontFrameClick(event) };
    this.trackClickHandler = { handler: event => this.handleTrackClick(event) };
    this.frontLayoutClickHandler = { handler: event => this.handleFrontLayoutClick(event) };
  }

  toggleInteractiveOnEachDoor(on, clickHandler) {
    this.product.doors.forEach(door => this.toggleInteractive(door.displayObject, on, clickHandler));
  }

  toggleInteractiveOnEachDoorPanel(on, clickHandler) {
    this.product.doors.forEach(door => super.toggleInteractiveOnEachDoorPanel(door, on, clickHandler));
  }

  activateInteraction(on) {
    //super.activateInteraction(on); Don't call super here!
    if (!this.product) {
      return;
    }

    switch (this.componentType) {
      case ComponentType.panel:
        switch (this.editScope) {
          case EditScope.front:
            this.toggleInteractive(this.product.displayObject, on, this.frontPanelsClickHandler.handler);
            break;
          case EditScope.door:
            this.toggleInteractiveOnEachDoor(on, this.doorPanelsClickHandler.handler);
            break;
          case EditScope.panel:
            this.toggleInteractiveOnEachDoorPanel(on, this.panelClickHandler.handler);
            break;
          default:
            this.unsupportedTypeAndScope();
            break;
        }
        break;
      case ComponentType.frame:
        switch (this.editScope) {
          case EditScope.front:
            this.toggleInteractive(this.product.displayObject, on, this.frontFrameClickHandler.handler);
            break;
          case EditScope.door:
            this.toggleInteractiveOnEachDoor(on, this.doorFrameClickHandler.handler);
            break;
          default:
            this.unsupportedTypeAndScope();
            break;
        }
        break;
      case ComponentType.track:
        if (this.editScope === EditScope.front) {
          this.toggleInteractive(this.product.displayObject, on, this.trackClickHandler.handler);
        } else {
          this.unsupportedTypeAndScope();
        }
        break;
      case ComponentType.layout:
        switch (this.editScope) {
          case EditScope.front:
            this.toggleInteractive(this.product.displayObject, on, this.frontLayoutClickHandler.handler);
            break;
          case EditScope.door:
            this.toggleInteractiveOnEachDoor(on, this.doorLayoutClickHandler.handler);
            break;
          default:
            this.unsupportedTypeAndScope();
            break;
        }
        break;
      default:
        this.unsupportedTypeAndScope();
        break;
    }
  }

  doOnTrackFinishChanged(track, oldFinish) {
    if (this.onTrackFinishChanged) {
      this.onTrackFinishChanged(this, oldFinish, track);
    }
  }

  replaceTrackFinish(track) {
    return track.replaceFinish(this.selectedValue, (trck, args) => this.doOnTrackFinishChanged(trck, args.oldFinish));
  }

  //#region Click Handlers

  handleFrontPanelsClick(event) {
    if (this.selectedValue) {
      const front = event.currentTarget.dataComponent;
      let wasChanged = false;
      front.replaceAllPanelsFinish(this.selectedValue, (frnt, args) => {
        this.doOnPanelFinishChanged(args.panel, args.oldFinish);
        wasChanged = true;
      });
      if (wasChanged) {
        this.doOnProductChanged(front);
      }
      this.doOnInteraction(event);
    }
  }

  handleFrontFrameClick(event) {
    if (this.selectedValue) {
      const front = event.currentTarget.dataComponent;
      let wasChanged = false;
      front.replaceFrameFinish(this.selectedValue, (door, { oldFinish }) => {
        this.doOnDoorFrameFinishChanged(door, oldFinish);
        wasChanged = true;
      });
      if (wasChanged) {
        //this.doOnProductChanged(door.frame);
        this.doOnProductChanged(front);
      }
      this.doOnInteraction(event);
    }
  }

  handleTrackClick(event) {
    if (this.selectedValue) {
      const front = event.currentTarget.dataComponent;
      const topChanged = this.replaceTrackFinish(front.topTrack);
      const botChanged = this.replaceTrackFinish(front.bottomTrack);
      if (topChanged || botChanged) {
        this.doOnProductChanged(front);
      }
      this.doOnInteraction(event);
    }
  }

  handleFrontLayoutClick(event) {
    if (this.selectedValue) {
      const front = event.currentTarget.dataComponent;
      let wasChanged = false;
      front.doors.forEach(door => {
        door.replaceLayout(this.selectedValue, (dor, { oldLayout }) => {
          this.doOnDoorLayoutChanged(dor, oldLayout);
        });
      });
      if (wasChanged) {
        this.doOnProductChanged(front);
      }
      this.doOnInteraction(event);
    }
  }

  //#endregion Click Handlers
}

